import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual'
import { type RefObject, useCallback } from 'react'

/** Default height for each option item in pixels */
const DEFAULT_OPTION_HEIGHT_PX = 36

/** Number of extra items to render above/below viewport for smooth scrolling */
const DEFAULT_OVERSCAN = 5

export interface UseVirtualListOptions {
  /** Total number of items in the list */
  count: number
  /** Reference to the scrollable container element */
  parentRef: RefObject<HTMLElement | null>
  /** Height of each item in pixels (default: 36) */
  estimateSize?: number
  /** Number of extra items to render above/below viewport (default: 5) */
  overscan?: number
  /** Enable/disable virtualization (default: true) */
  enabled?: boolean
}

export interface UseVirtualListResult {
  /** Array of virtual items currently visible (plus overscan) */
  virtualItems: VirtualItem[]
  /** Total height of all items for scrollbar sizing */
  totalSize: number
  /** Scroll to a specific item index */
  scrollToIndex: (index: number, options?: { align?: 'start' | 'center' | 'end' | 'auto' }) => void
  /** Measure element at index (for dynamic heights if needed) */
  measureElement: (element: HTMLElement | null) => void
}

/**
 * Hook for virtualizing a list of items, rendering only visible items in the DOM.
 * Wraps @tanstack/react-virtual with project-specific defaults.
 *
 * @example
 * ```tsx
 * const { virtualItems, totalSize } = useVirtualList({
 *   count: records.length,
 *   parentRef: scrollContainerRef,
 * })
 * ```
 */
export function useVirtualList({
  count,
  parentRef,
  estimateSize = DEFAULT_OPTION_HEIGHT_PX,
  overscan = DEFAULT_OVERSCAN,
  enabled = true,
}: UseVirtualListOptions): UseVirtualListResult {
  const virtualizer = useVirtualizer({
    count: enabled ? count : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => estimateSize, [estimateSize]),
    overscan,
  })

  return {
    virtualItems: virtualizer.getVirtualItems(),
    totalSize: virtualizer.getTotalSize(),
    scrollToIndex: virtualizer.scrollToIndex,
    measureElement: virtualizer.measureElement,
  }
}
